import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  PaginatedDropdownLoadParams,
  PaginatedOptionsPage,
} from '@shared/utils/paginated-dropdown.util';
import { CompanyItem, CompanyListResponse } from '../company/company.config';
import {
  SalesOrganizationControllerService,
  PageRequestDTO,
  SalesOrganizationListDTO,
  SalesOrganizationWithGroupDTO,
  SalesOrganizationDTO,
  PageInfoSalesOrganizationListVO,
} from '@app/api/channel-service';
import {
  GroupManagementService,
  KeycloakGroupRequest,
  KeycloakGroupResponse,
  PageInfoKeycloakGroupResponse,
} from '@app/api/user-service';

export interface SearchFormData {
  code?: string;
  name?: string;
  status?: string;
  page?: number;
  size?: number;
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private readonly apiSalesOrganizationControllerService = inject(
    SalesOrganizationControllerService,
  );
  private readonly apiGroupManagementService = inject(GroupManagementService);

  // ============================================================================
  // COMPANY (Sales Organization) METHODS
  // ============================================================================

  getCompanyOptionsPage(
    params: PaginatedDropdownLoadParams,
  ): Observable<PaginatedOptionsPage<string>> {
    const searchText = params.searchText?.trim();
    return this.searchCompanies({
      page: params.page - 1,
      size: params.size,
      name: searchText || undefined,
      status: 'ACTIVE',
    }).pipe(
      map((response) => ({
        options: response.data.map((c) => ({
          label: c.name,
          value: c.id,
        })),
        pageContentLength: response.data.length,
        totalElements: response.totalRecords,
      })),
      catchError(() => of({ options: [], pageContentLength: 0 })),
    );
  }

  searchCompanies(params: SearchFormData): Observable<CompanyListResponse> {
    let status: boolean | undefined;

    if (params.status === 'ACTIVE') {
      status = true;
    } else if (params.status === 'INACTIVE') {
      status = false;
    }

    const salesOrganizationListDTO: SalesOrganizationListDTO = {
      orgCode: params.code,
      orgName: params.name,
      status,
    };

    const pageRequest: PageRequestDTO = {
      page: params.page ?? 0,
      size: params.size ?? 20,
    };

    return this.apiSalesOrganizationControllerService
      .pageSalesOrganizationList(
        salesOrganizationListDTO,
        pageRequest,
        'body',
        false,
        {
          httpHeaderAccept: 'application/json' as any,
        },
      )
      .pipe(
        map(
          (response: PageInfoSalesOrganizationListVO): CompanyListResponse => {
            const companies: CompanyItem[] =
              response.pageContent?.map((item) => ({
                id: item.id?.toString() ?? '',
                code: item.orgCode ?? '',
                name: item.orgName ?? '',
                status: item.status ? 'ACTIVE' : 'INACTIVE',
                creationDate: item.creationDate ?? '',
                lastModifiedDate: undefined,
                shopPermitName: item.shopPermitName ?? '',
              })) ?? [];

            return {
              data: companies,
              totalRecords: response.totalElements ?? 0,
              loading: false,
            };
          },
        ),
      );
  }

  getCompanyById(id: string): Observable<CompanyItem> {
    const numericId = Number.parseInt(id, 10);

    return this.apiSalesOrganizationControllerService
      .getSalesOrganizationDetail(numericId, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((response: any) => ({
          id: response.id?.toString() ?? '',
          code: response.orgCode ?? '',
          name: response.orgName ?? '',
          legalPerson: response.legalPerson ?? '',
          businessLicense: response.businessLicense ?? '',
          shopPermitName: response.shopPermitName ?? '',
          status: response.status ? 'ACTIVE' : 'INACTIVE',
          creationDate: response.creationDate ?? '',
          lastModifiedDate: response.lastUpdateDate ?? '',
        })),
      );
  }

  createCompany(
    company: Partial<
      CompanyItem & {
        legalPerson?: string;
        businessLicense?: string;
        shopPermitName?: string;
      }
    >,
  ): Observable<CompanyItem> {
    const salesOrganizationWithGroupDTO: SalesOrganizationWithGroupDTO = {
      orgCode: company.code,
      orgName: company.name,
      legalPerson: company.legalPerson,
      businessLicense: company.businessLicense,
      shopPermitName: company.shopPermitName,
      status: company.status === 'ACTIVE',
    };

    return this.apiSalesOrganizationControllerService
      .createSalesOrganization(salesOrganizationWithGroupDTO)
      .pipe(
        map((response: any) => ({
          id: response.id?.toString() ?? '',
          code: response.orgCode ?? '',
          name: response.orgName ?? '',
          legalPerson: response.legalPerson ?? '',
          businessLicense: response.businessLicense ?? '',
          status: response.status ? 'ACTIVE' : 'INACTIVE',
          creationDate: response.creationDate ?? '',
          lastModifiedDate: response.lastUpdateDate,
        })),
      );
  }

  updateCompany(
    id: string,
    company: Partial<
      CompanyItem & {
        legalPerson?: string;
        businessLicense?: string;
        shopPermitName?: string;
      }
    >,
  ): Observable<CompanyItem> {
    const salesOrganizationDTO: SalesOrganizationDTO = {
      id: Number.parseInt(id, 10),
      orgCode: company.code,
      orgName: company.name,
      legalPerson: company.legalPerson,
      businessLicense: company.businessLicense,
      shopPermitName: company.shopPermitName,
      status: company.status === 'ACTIVE',
    };

    return this.apiSalesOrganizationControllerService
      .updateSalesOrganization(salesOrganizationDTO)
      .pipe(
        map((response: any) => ({
          id: response.id?.toString() ?? '',
          code: response.orgCode ?? '',
          name: response.orgName ?? '',
          legalPerson: response.legalPerson ?? '',
          businessLicense: response.businessLicense ?? '',
          status: response.status ? 'ACTIVE' : 'INACTIVE',
          creationDate: response.creationDate ?? '',
          lastModifiedDate: response.lastUpdateDate,
        })),
      );
  }

  deleteCompany(id: string): Observable<void> {
    const numericId = Number.parseInt(id, 10);
    return this.apiSalesOrganizationControllerService.deleteById1(numericId);
  }

  // ============================================================================
  // GROUP METHODS
  // ============================================================================

  getGroups(
    page?: number,
    size?: number,
    search?: string,
  ): Observable<PageInfoKeycloakGroupResponse> {
    return this.apiGroupManagementService.getGroups(page, size, search);
  }

  getGroupById(groupId: string): Observable<KeycloakGroupResponse> {
    return this.apiGroupManagementService.getGroup(groupId);
  }

  getGroupByName(groupName: string): Observable<KeycloakGroupResponse> {
    return this.apiGroupManagementService.getGroupByName(groupName);
  }

  createGroup(
    groupRequest: KeycloakGroupRequest,
  ): Observable<KeycloakGroupResponse> {
    return this.apiGroupManagementService.createGroup(groupRequest);
  }

  updateGroup(
    groupId: string,
    groupRequest: KeycloakGroupRequest,
  ): Observable<KeycloakGroupResponse> {
    return this.apiGroupManagementService.updateGroup(groupId, groupRequest);
  }

  deleteGroup(groupId: string): Observable<void> {
    return this.apiGroupManagementService.deleteGroup(groupId);
  }
}
